import * as React from 'react'
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'

import { MoreHorizontal, Plus } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import DynamicOverflowListStore from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList.store'
import { InfoIcon } from '~/client/src/shared/components/icons/InfoIcon'
import { SavedViewsConfigureIcon } from '~/client/src/shared/components/icons/SavedViewsConfigureIcon'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'

import DesktopInitialState from '../../stores/DesktopInitialState'
import ManageSavedViewsDialog from './ManageSavedViewsDialog'
import NewViewDialog from './NewViewDialog'
import {
  DeleteFilterMutationFn,
  FilterTypeDescriptor,
  SaveFilterMutationFn,
} from './savedViewsTypes'
import { SavedView } from './savedViewsUtils'
import { useSavedViews } from './useSavedViews'

import './SavedViews.scss'

export interface PresetTab {
  id: string
  title: string
  icon?: React.ReactNode
  tooltip?: string | JSX.Element
}

interface SavedViewsOwnProps {
  views: SavedView[]
  onViewSelect: (
    filters: { [key: string]: Set<string> },
    groupBy: string,
  ) => void
  className?: string
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications'
  saveFilterMutation?: SaveFilterMutationFn
  deleteFilterMutation?: DeleteFilterMutationFn
  refetchQueryName?: string
  appliedFilters?: { [facetKey: string]: Set<string> }
  currentGrouping?: string
  facets?: FilterTypeDescriptor[]
  groupingOptions?: IOptionItem[]
  groupingEnabled?: boolean
  localStorageKey?: LocalStorageKey
  presetTabs?: PresetTab[]
  defaultPresetTabId?: string
  onPresetTabSelect?: (tabId: string) => void
  hideActions?: boolean
}

interface SavedViewsInjectedProps {
  userProjectsStore?: UserProjectsStore
  state?: DesktopInitialState
}

type SavedViewsProps = SavedViewsOwnProps & SavedViewsInjectedProps

export interface SavedViewsRef {
  reset: () => void
  selectView: (viewId: string | null) => void
}

const MORE_BTN_WIDTH = 32

const SavedViews = forwardRef<SavedViewsRef, SavedViewsProps>(
  (
    {
      views,
      onViewSelect,
      className,
      filterType,
      saveFilterMutation,
      deleteFilterMutation,
      refetchQueryName,
      appliedFilters = {},
      currentGrouping = '',
      facets = [],
      groupingOptions = [],
      groupingEnabled = true,
      localStorageKey,
      presetTabs,
      defaultPresetTabId,
      onPresetTabSelect,
      hideActions,
      userProjectsStore,
      state,
    },
    ref,
  ) => {
    const [isOverflowedModalShown, setIsOverflowedModalShown] = useState(false)

    const dynamicOverflowListStore = useMemo(
      () => new DynamicOverflowListStore(),
      [],
    )

    const presetTabIds = useMemo(
      () =>
        presetTabs?.length ? new Set(presetTabs.map(t => t.id)) : undefined,
      [presetTabs],
    )

    const userActiveProjectSettings = state?.userActiveProjectSettings
    const isActiveUserAdmin = state?.userActiveProjectSettings?.isAdmin || false

    const savedViews = useSavedViews({
      views,
      filterType,
      userActiveProjectSettings,
      userProjectsStore,
      saveFilterMutation,
      deleteFilterMutation,
      refetchQueryName,
      projectId: state?.activeProject?.id,
      userId: state?.user?.id,
      isActiveUserAdmin,
      appliedFilters,
      currentGrouping,
      groupingOptions,
      onApplyFilters: onViewSelect,
      localStorageKey,
      projectCode: state?.activeProject?.code,
      presetTabIds,
      defaultSelectedId: defaultPresetTabId ?? presetTabs?.[0]?.id ?? null,
    })

    useImperativeHandle(
      ref,
      () => ({
        reset: savedViews.handleReset,
        selectView: (viewId: string | null) => {
          savedViews.handleViewSelect(viewId)
          if (viewId && presetTabIds?.has(viewId)) {
            onPresetTabSelect?.(viewId)
          }
        },
      }),
      [
        savedViews.handleReset,
        savedViews.handleViewSelect,
        presetTabIds,
        onPresetTabSelect,
      ],
    )

    const hasPresetTabs = presetTabs && presetTabs.length > 0

    const allTabs: Array<{
      title: string
      id: string | null
      icon?: React.ReactNode
      tooltip?: string | JSX.Element
      isPreset?: boolean
    }> = [
      ...(hasPresetTabs
        ? presetTabs.map(t => ({
            title: t.title,
            id: t.id,
            icon: t.icon,
            tooltip: t.tooltip,
            isPreset: true,
          }))
        : [
            {
              title: Localization.translator.all_items,
              id: null as string | null,
            },
          ]),
      ...savedViews.sortedVisibleViews.map(view => ({
        title: view.name,
        id: view.id,
      })),
    ]

    const handleTabClick = (tabId: string | null) => {
      savedViews.handleViewSelect(tabId)
      if (tabId && presetTabIds?.has(tabId)) {
        onPresetTabSelect?.(tabId)
      }
    }

    const tabsAsElements = allTabs.map((tab, idx) => (
      <div
        key={tab.id || idx}
        className={classList({
          'tw:flex tw:items-center tw:[&>*]:flex-1 tw:justify-center tw:cursor-pointer tw:flex-none tw:h-full tw:text-label-lg tw:relative': true,
          'tw:text-primary tw:after:content-[""] tw:after:absolute tw:after:bottom-0 tw:after:left-0 tw:after:right-0 tw:after:h-0.5 tw:after:bg-primary':
            tab.id === savedViews.selectedViewId,
          'tw:text-muted-foreground': tab.id !== savedViews.selectedViewId,
        })}
        onClick={() => {
          handleTabClick(tab.id)
          setIsOverflowedModalShown(false)
        }}
      >
        {tab.icon}
        <span className="tw:px-5 tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis">
          {tab.title}
          {tab.tooltip && (
            <TooltipWrapper
              content={<div className="tw:max-w-[240px]">{tab.tooltip}</div>}
            >
              <InfoIcon
                size="xs"
                className="tw:inline-block tw:align-middle tw:ml-1"
              />
            </TooltipWrapper>
          )}
        </span>
      </div>
    ))

    const renderOverflowedTabs = (hiddenElements: JSX.Element[]) => {
      if (!hiddenElements?.length) {
        return null
      }

      const isMoreBtnActive = hiddenElements.some(
        el => el.key === savedViews.selectedViewId,
      )

      return (
        <Popover
          open={isOverflowedModalShown}
          onOpenChange={setIsOverflowedModalShown}
        >
          <PopoverTrigger
            aria-label={Localization.translator.more}
            render={
              <Button
                variant={isMoreBtnActive ? 'default' : 'outline'}
                size="icon"
              />
            }
          >
            <MoreHorizontal />
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            sideOffset={4}
            className="tw:w-auto tw:flex tw:flex-col tw:max-h-[360px] tw:overflow-auto"
          >
            {hiddenElements.map(item => (
              <div key={item.key} className="tw:py-2 tw:px-1">
                {item}
              </div>
            ))}
          </PopoverContent>
        </Popover>
      )
    }

    return (
      <div
        className={classList({
          'tw:flex tw:items-center tw:[&>*]:flex-1 tw:justify-start tw:relative tw:select-none tw:h-[55px]': true,
          [className]: !!className,
        })}
      >
        <DynamicOverflowList
          items={tabsAsElements}
          store={dynamicOverflowListStore}
          elementsClassName="full-height vertical-align-middle"
          moreButtonWidth={MORE_BTN_WIDTH}
          allowRecalculationForItems
          shouldIgnoreScrollBarWidth
          shouldUseItemKey
        >
          {({
            visibleElements,
            overflowElements,
            containerRefSetter,
            setAdditionalElementsRef,
          }) => (
            <div
              className={classList({
                'tw:h-full': true,
                nowrap: visibleElements.length === 1,
              })}
              ref={containerRefSetter}
            >
              {visibleElements}
              <div className="tw:inline-block tw:align-middle">
                {renderOverflowedTabs(overflowElements)}
              </div>
              <div
                className="tw:inline-block tw:align-middle"
                ref={setAdditionalElementsRef}
              >
                {!hideActions && (
                  <div className="tw:flex tw:items-center tw:[&>*]:flex-1 tw:gap-5 tw:ml-4 tw:text-muted-foreground saved-filters">
                    <Popover
                      open={savedViews.isNewDialogOpen}
                      onOpenChange={open => {
                        if (open) {
                          savedViews.handleAddClick()
                        } else {
                          savedViews.handleCancelDialog()
                        }
                      }}
                    >
                      <PopoverTrigger
                        aria-label={Localization.translator.new}
                        title={Localization.translator.new}
                        render={<Button variant="ghost" size="icon" />}
                      >
                        <Plus />
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="start"
                        sideOffset={8}
                        className="tw:w-auto tw:bg-transparent tw:p-0 tw:shadow-none tw:ring-0"
                      >
                        <NewViewDialog
                          viewName={savedViews.viewName}
                          appliedFilters={appliedFilters}
                          groupByCaption={savedViews.groupByCaption}
                          isSaving={savedViews.isSaving}
                          saveError={savedViews.saveError}
                          onCreateClick={savedViews.handleCreateView}
                          onCancelClick={savedViews.handleCancelDialog}
                          onNameChange={savedViews.handleNameChange}
                          onIsPublicToggle={savedViews.handleTogglePublic}
                          isPublic={savedViews.isPublic}
                          isActiveUserAdmin={savedViews.isActiveUserAdmin}
                          facets={facets}
                          groupingEnabled={groupingEnabled}
                        />
                      </PopoverContent>
                    </Popover>

                    <Popover
                      open={savedViews.isManageDialogOpen}
                      onOpenChange={open => {
                        if (open) {
                          savedViews.handleManageDialogOpen()
                        } else {
                          savedViews.handleManageDialogClose()
                        }
                      }}
                    >
                      <PopoverTrigger
                        aria-label={Localization.translator.savedFilters}
                        title={Localization.translator.savedFilters}
                        render={<Button variant="ghost" size="icon" />}
                      >
                        <SavedViewsConfigureIcon />
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="start"
                        sideOffset={8}
                        className="tw:w-auto tw:bg-transparent tw:p-0 tw:shadow-none tw:ring-0"
                      >
                        <ManageSavedViewsDialog
                          views={views}
                          selectedViewId={savedViews.selectedViewId}
                          onClose={savedViews.handleManageDialogClose}
                          onViewSelect={handleTabClick}
                          isActiveUserAdmin={savedViews.isActiveUserAdmin}
                          currentUserId={state?.user?.id}
                          facets={facets}
                          groupingOptions={groupingOptions}
                          groupingEnabled={groupingEnabled}
                          onDeleteView={savedViews.deleteView}
                          onDuplicateView={savedViews.duplicateView}
                          onRenameView={savedViews.renameView}
                          onToggleVisibility={savedViews.toggleViewVisibility}
                          onReorderViews={savedViews.reorderViews}
                          onUpdateViewAccess={savedViews.updateViewAccess}
                          allViewsForDialog={savedViews.allViewsForDialog}
                          presetTabs={presetTabs}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
          )}
        </DynamicOverflowList>
      </div>
    )
  },
)

export default inject('userProjectsStore', 'state')(observer(SavedViews))
